import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from "recharts";
import { useApi } from "@/lib/api";
import { PageHeader, SectionTitle, PageLoader, StatusBadge, Btn, EmptyState, Panel } from "@/lib/ui";
import { BRAND_ACCENTS } from "@/lib/ui";
import { LineChart, Activity, ArrowLeft, Plug, BarChart3, Users, Eye, MousePointerClick, TrendingUp } from "lucide-react";

const hsl = (v) => `hsl(${v || "46 68% 52%"})`;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

function ConnCard({ cell, icon: Icon }) {
  const on = cell.status === "connected";
  return (
    <div className="neu-card rounded-2xl p-5" data-testid={`portal-conn-${cell.platform}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><SectionTitle>{cell.label}</SectionTitle></div>
        {on ? <StatusBadge tone="ok" dot>Connected</StatusBadge> : <StatusBadge tone="muted" dot>Not connected</StatusBadge>}
      </div>
      {on ? (
        <div className="space-y-1.5">
          {Object.entries(cell.config).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground font-mono">{k}</span>
              <span className="text-foreground/85 font-mono truncate max-w-[60%]">{typeof v === "boolean" ? (v ? "set" : "—") : (v || "—")}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Add the {cell.label} ID on the Analytics &amp; Pixels page to connect this portal.</p>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="neu-inset rounded-xl p-4">
      <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      <div className="font-head text-2xl font-bold mt-2" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function GA4Report({ brand, accent }) {
  const api = useApi();
  const { data, isLoading } = useQuery({ queryKey: ["ga4-report", brand], queryFn: () => api.get(`/growth/portal/${brand}/report?days=28`) });
  if (isLoading) return <PageLoader />;
  const status = data?.status;

  if (status === "not_connected")
    return <div className="neu-card rounded-2xl p-1"><EmptyState icon={BarChart3} title="No GA4 property set" hint="Add this portal's GA4 Property ID on the Analytics & Pixels page." /></div>;
  if (status === "not_configured")
    return <div className="neu-card rounded-2xl p-1"><EmptyState icon={Plug} title="GA4 credentials not configured" hint="The Google service-account key isn't set on the server yet. Once it's added, live metrics appear here automatically." /></div>;
  if (status === "error")
    return <div className="neu-card rounded-2xl p-1"><EmptyState icon={Activity} title="GA4 report unavailable" hint={data.detail || "Check the property ID and that the service account has Viewer access."} /></div>;

  const t = data.totals || {};
  const series = data.series || [];
  const engagement = t.engagement_rate ? `${(parseFloat(t.engagement_rate) * 100).toFixed(1)}%` : "—";
  const channels = data.channels || [];
  const maxCh = Math.max(1, ...channels.map((c) => c.sessions));

  return (
    <div className="space-y-4" data-testid="ga4-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Activity} label="Sessions · 28d" value={fmt(t.sessions)} accent={accent} />
        <StatTile icon={Users} label="Active Users" value={fmt(t.active_users)} accent={accent} />
        <StatTile icon={Eye} label="Page Views" value={fmt(t.page_views)} accent={accent} />
        <StatTile icon={MousePointerClick} label="Engagement" value={engagement} accent={accent} />
      </div>

      <div className="neu-card rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-primary" /><SectionTitle>Sessions · last 28 days</SectionTitle></div>
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="ga4grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "hsl(0 0% 55%)", fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={(d) => (d && d.length === 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}` : d)} minTickGap={24} />
              <YAxis tick={{ fill: "hsl(0 0% 55%)", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 15%)", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="sessions" stroke={accent} strokeWidth={2} fill="url(#ga4grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="neu-card rounded-2xl p-5">
          <SectionTitle>Top pages</SectionTitle>
          <div className="mt-3 space-y-2">
            {(data.top_pages || []).length === 0 ? <div className="text-xs text-muted-foreground">No data.</div>
              : data.top_pages.map((p, i) => (
                <div key={p.path} className="flex items-center justify-between gap-3 text-xs">
                  <span className="truncate font-mono text-foreground/80">{p.path}</span>
                  <span className="shrink-0 text-muted-foreground">{fmt(p.views)}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="neu-card rounded-2xl p-5">
          <SectionTitle>Traffic sources</SectionTitle>
          <div className="mt-3 space-y-2.5">
            {channels.length === 0 ? <div className="text-xs text-muted-foreground">No data.</div>
              : channels.map((c) => (
                <div key={c.channel} data-testid={`ga4-channel-${c.channel}`}>
                  <div className="flex items-center justify-between text-xs mb-1"><span className="text-foreground/80">{c.channel}</span><span className="text-muted-foreground">{fmt(c.sessions)}</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.sessions / maxCh) * 100}%`, background: accent }} /></div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GrowthPortal() {
  const { brand } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["growth-portal", brand], queryFn: () => api.get(`/growth/portal/${brand}`) });

  if (isLoading) return <PageLoader />;
  if (!data) return <EmptyState icon={Plug} title="Portal not found" />;
  const accent = hsl(BRAND_ACCENTS[data.code] || data.accent);

  return (
    <div data-testid="growth-portal-page">
      <button onClick={() => navigate("/growth")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="growth-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Analytics & Pixels
      </button>
      <PageHeader kicker="Growth & Marketing · Portal" title={data.name}>
        <span className="h-3 w-3 rounded-sm" style={{ background: accent }} />
        <StatusBadge tone={data.state === "ACTIVE" ? "ok" : "muted"}>{data.state === "ACTIVE" ? "Active" : "Soon"}</StatusBadge>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ConnCard cell={data.ga4} icon={LineChart} />
        <ConnCard cell={data.meta_pixel} icon={Activity} />
      </div>

      <div className="flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-primary" /><SectionTitle>Performance · Google Analytics</SectionTitle></div>
      <GA4Report brand={data.code} accent={accent} />
    </div>
  );
}
